import type { ApiErrorBody } from '@adega/shared';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Quanto tempo esperar antes de desistir de uma requisição.
 *
 * `fetch` não tem prazo: sem isto, ele fica pendurado até o sistema operacional
 * desistir sozinho. Com rota quebrada — o pacote sai e nada volta, que é
 * exatamente o caso do cluster fora de alcance — isso passa de um minuto. A
 * pessoa toca no botão, não acontece nada, e o erro chega tarde demais para
 * dizer alguma coisa. Quinze segundos é folgado para a rede de casa e curto o
 * bastante para o erro ainda fazer sentido na cabeça de quem tocou.
 */
const ESPERA_PADRAO_MS = 15_000;

/** Erro da API com o `code` estável, para a UI reagir a casos específicos. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Falha em alcançar o servidor — diferente de o servidor responder "não". */
export function semConexao(erro: unknown): boolean {
  return erro instanceof ApiError && (erro.code === 'NETWORK_ERROR' || erro.code === 'TIMEOUT');
}

interface Opcoes {
  /** Prazo próprio, para as chamadas que são lentas por natureza (IA, upload). */
  esperaMs?: number;
}

async function request<T>(caminho: string, init?: RequestInit, opcoes?: Opcoes): Promise<T> {
  const esperaMs = opcoes?.esperaMs ?? ESPERA_PADRAO_MS;
  const controle = new AbortController();
  const prazo = setTimeout(() => controle.abort(), esperaMs);
  let resposta: Response;

  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      ...init,
      signal: controle.signal,
      // A sessão vive num cookie httpOnly; sem isto ela não acompanha a
      // requisição quando a API está em outra origem.
      credentials: 'include',
      headers: {
        // Content-Type só quando há corpo JSON de fato. Declarar JSON num POST sem
        // corpo (ex.: "analisar com IA", "sair") faz o Fastify recusar a
        // requisição; em FormData o navegador define o boundary sozinho.
        ...(init?.body !== undefined && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...init?.headers,
      },
    });
  } catch {
    // Estourar o prazo e não achar o servidor são coisas diferentes para quem
    // lê: uma fala de demora, a outra de alcance. Confundir as duas manda a
    // pessoa procurar o problema no lugar errado.
    if (controle.signal.aborted) {
      throw new ApiError(
        'TIMEOUT',
        `O servidor da adega não respondeu em ${Math.round(esperaMs / 1000)} segundos. Deve ser a conexão.`,
        0,
      );
    }
    throw new ApiError(
      'NETWORK_ERROR',
      'Sem conexão com o servidor da adega. Verifique a rede do aparelho.',
      0,
    );
  } finally {
    // A partir daqui a resposta já começou a chegar: o prazo cumpriu o papel.
    clearTimeout(prazo);
  }

  if (resposta.status === 204) {
    return undefined as T;
  }

  const texto = await resposta.text();
  const corpo: unknown = texto ? JSON.parse(texto) : null;

  if (!resposta.ok) {
    const erro = (corpo as ApiErrorBody | null)?.error;
    throw new ApiError(
      erro?.code ?? 'UNKNOWN',
      erro?.message ?? 'Erro inesperado.',
      resposta.status,
      erro?.details,
    );
  }

  return corpo as T;
}

export const api = {
  get: <T>(caminho: string, opcoes?: Opcoes) => request<T>(caminho, undefined, opcoes),
  post: <T>(caminho: string, body?: unknown, opcoes?: Opcoes) =>
    request<T>(
      caminho,
      { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) },
      opcoes,
    ),
  patch: <T>(caminho: string, body: unknown, opcoes?: Opcoes) =>
    request<T>(caminho, { method: 'PATCH', body: JSON.stringify(body) }, opcoes),
  delete: <T = void>(caminho: string, opcoes?: Opcoes) =>
    request<T>(caminho, { method: 'DELETE' }, opcoes),
  upload: <T>(caminho: string, arquivo: File, opcoes?: Opcoes) => {
    const form = new FormData();
    form.append('file', arquivo);
    return request<T>(caminho, { method: 'POST', body: form }, opcoes);
  },
};

/**
 * Prazos longos: IA e upload demoram por natureza, não por defeito.
 *
 * Um provedor de IA leva dezenas de segundos numa análise grande, e foto de
 * rótulo sobe devagar em 5G. Cortar essas chamadas no prazo padrão seria
 * inventar um problema de rede onde só havia paciência.
 */
export const ESPERA_DA_IA = { esperaMs: 120_000 };
export const ESPERA_DO_UPLOAD = { esperaMs: 90_000 };
